import { useMemo, type ReactNode } from 'react';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { exportQueryOf, useListSearch } from '@eis/core';
import {
  ExportButton,
  InfiniteDataGrid,
  PageHeader,
  PageLayout,
  PropertyFilter,
  RequirePermission,
  cardSurfaceSx,
  useGridExportColumns,
  type ExportableGridColDef,
} from '@eis/patterns';
import type { ConfigApi } from '@eis/insuranceservice-sdk';
import { insurancePermissions as p } from '../../../permissions';
import { useFilterCatalog, useInfiniteList } from '../api/tarifs';

const SORT_KEYS: Readonly<Record<string, string>> = { name: 'name', slug: 'slug' };

/**
 * Every tarif in the tenant — the entry point of the Produkte surface, on the list standard
 * (SDD-017 §9): the property filter over the tarif catalog, the standard search, server order and
 * one scroll, all in the URL.
 *
 * A tarif's identity never changes meaning: the versioned content hangs off its generations, so
 * this grid deliberately shows no version, no digest and no status. Putting an "active version"
 * column here would be summarising several generations into one cell and getting it wrong for
 * every tarif that has more than one.
 */
export function TarifListPage(): ReactNode {
  const { t } = useTranslation('insurance');
  const navigate = useNavigate();
  const url = useListSearch();
  const catalog = useFilterCatalog('tarifs');
  const list = useInfiniteList('tarifs', url.params);

  // Each column is a field of `Tarif` (SDD-020 §8.1): the export writes what the grid shows.
  const columns = useMemo<ExportableGridColDef<ConfigApi.Tarif>[]>(
    () => [
      {
        field: 'name',
        headerName: t('tarifs.list.name'),
        flex: 1,
        minWidth: 200,
        exportPath: 'name',
      },
      { field: 'slug', headerName: t('tarifs.list.slug'), width: 180, exportPath: 'slug' },
      {
        field: 'ern',
        headerName: t('tarifs.list.ern'),
        exportPath: 'ern',
        flex: 1,
        minWidth: 260,
        // The ERN is an address, not prose: it is the thing an operator copies into a manifest.
        renderCell: ({ row }) => (
          <Typography variant="body2" component="span" sx={{ fontFamily: 'monospace' }}>
            {row.ern}
          </Typography>
        ),
      },
    ],
    [t],
  );
  const exportColumns = useGridExportColumns(columns);

  const header = (
    <PageHeader
      title={t('tarifs.title')}
      description={t('tarifs.subtitle')}
      actions={
        // Either action reaches the same chooser; a persona holding one and not the other still
        // needs a way in, and the chooser is where the branch is gated (PERMISSIONS.md).
        <Stack direction="row" spacing={1}>
          <ExportButton
            service="insurance"
            permission={p.exports.create}
            rowCount={list.isLoading ? undefined : list.items.length}
            buildRequest={(format) => ({
              collection: 'tarifs',
              query: exportQueryOf(url.params),
              columns: exportColumns,
              format,
              label: t('tarifs.title'),
            })}
          />
          <RequirePermission groups={[[p.products.create], [p.groupTariffs.create]]}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                void navigate({ to: '/insurance/tarifs/new' });
              }}
            >
              {t('authoring.create.action')}
            </Button>
          </RequirePermission>
        </Stack>
      }
    />
  );

  return (
    <PageLayout header={header}>
      <Paper variant="outlined" sx={(theme) => ({ p: 2.5, ...cardSurfaceSx(theme) })}>
        <Stack spacing={2}>
          <PropertyFilter
            catalog={catalog.data}
            value={{ filters: url.params.filters ?? '', search: url.params.search ?? '' }}
            onChange={(next) => {
              url.set({ filters: next.filters, search: next.search });
            }}
          />
          <InfiniteDataGrid
            rows={list.items}
            list={list}
            columns={columns}
            getRowId={(row) => row.slug ?? row.ern ?? ''}
            ariaLabel={t('tarifs.list.ariaLabel')}
            order={url.params.order}
            onOrderChange={(order) => {
              url.set({ order });
            }}
            sortKeys={SORT_KEYS}
            errorDescription={t('tarifs.error.list')}
            emptyDescription={t('tarifs.list.empty')}
            onRowClick={(row) => {
              const slug = row.slug ?? '';
              if (slug === '') return;
              void navigate({ to: '/insurance/tarifs/$slug', params: { slug } });
            }}
          />
        </Stack>
      </Paper>
    </PageLayout>
  );
}
